<!--
  FrameX Pages tab: hosts the platform's PageList (shared with legacy
  DocumentProperties) verbatim, plus a Select all / Invert / Clear header of
  its own (kept out of PageList to stay legacy-safe). The Frames-tab row
  highlighting is opt-in via PageList's `fxRowStyle` (default off), so legacy
  callers are unchanged.
-->
<script>
  import PageList from "#lib/sidebar/PageList.svelte";
  import { orderedList } from "@local/extensions/collections/sortable-list.js";
  import { selectedPages } from "#src/stores/ui.js";
  import { selectAllIds, invertSelection } from "#lib/framex/selection.js";

  export let group;
  export let doc;
  export let isVirtual = false;
  export let docLocations;
  export let disabled;
  export let filters;
  export let hoveredPage = null;
  export let lastSelectedPage = null;

  // Same visibility rule as PageList's own `filterPages` — a page counts as
  // visible when it isn't permanently hidden and isn't hidden by the
  // current filter set. Kept in step with PageList rather than exposed from
  // it, since PageList doesn't emit its visible-id list today.
  $: visiblePages = doc
    ? orderedList(doc.data.pages)
        .filter((p) => !p.hidden && !filters?.hidden_pages?.[p.id])
        .map((p) => p.id)
    : [];

  function selectAll() {
    selectedPages.set(selectAllIds(visiblePages));
  }

  function invert() {
    selectedPages.set(invertSelection(visiblePages, $selectedPages));
  }

  function clear() {
    selectedPages.set({});
  }
</script>

<div class="fx-pages-tab">
  <div class="fx-pages-header">
    <button type="button" class="fx-text-link" on:click={selectAll}>Select all</button>
    <button type="button" class="fx-text-link" on:click={invert}>Invert</button>
    <button type="button" class="fx-text-link" on:click={clear}>Clear</button>
  </div>

  <PageList
    {group}
    {doc}
    {isVirtual}
    {docLocations}
    {disabled}
    {filters}
    fxRowStyle
    bind:hoveredPage
    bind:lastSelectedPage
    on:zoom-to-page
    on:update-visible-pages />
</div>

<style lang="scss">
  .fx-pages-tab {
    user-select: none;
    // Match the legacy text-xs base (<SidebarSection>) so the page list and
    // its links inherit 12px / 1rem line-height, not the browser default.
    font-size: 12px;
    line-height: 1rem;
  }

  // The page list's inline-rename field (and any other input) stays
  // selectable; only row-label range-selection is suppressed.
  .fx-pages-tab :global(input),
  .fx-pages-tab :global(textarea),
  .fx-pages-tab :global([contenteditable="true"]) {
    user-select: text;
  }

  .fx-pages-header {
    display: flex;
    gap: 12px;
    align-items: center;
    padding: 6px 16px 2px;
  }
</style>
